/**
 * Source authority detection and badge utilities shared across verdict components.
 */

export interface SourceBadge {
  type: 'government' | 'academic' | 'research' | 'news' | 'standard';
  label: string;
  color: string;
  bgColor: string;
}

/** Determine source authority badge based on URL domain */
export function getSourceBadge(url: string): SourceBadge {
  try {
    const hostname = new URL(url).hostname.toLowerCase();

    // Government sources (.gov)
    if (hostname.endsWith('.gov') || hostname.endsWith('.gov.uk') || hostname.endsWith('.gov.au')) {
      return { type: 'government', label: 'GOV', color: 'text-blue-400', bgColor: 'bg-blue-400/10 border-blue-400/30' };
    }

    // Educational sources (.edu)
    if (hostname.endsWith('.edu') || hostname.endsWith('.ac.uk') || hostname.endsWith('.edu.au')) {
      return { type: 'academic', label: 'EDU', color: 'text-purple-400', bgColor: 'bg-purple-400/10 border-purple-400/30' };
    }

    // Research/Academic publishers
    const academicDomains = ['pubmed', 'ncbi.nlm.nih', 'nature.com', 'science.org', 'sciencedirect',
      'springer', 'wiley', 'arxiv.org', 'jstor.org', 'researchgate', 'scholar.google',
      'nejm.org', 'thelancet.com', 'bmj.com', 'jama', 'cell.com', 'plos.org', 'pnas.org'];
    if (academicDomains.some(d => hostname.includes(d))) {
      return { type: 'research', label: 'RESEARCH', color: 'text-emerald-400', bgColor: 'bg-emerald-400/10 border-emerald-400/30' };
    }

    // Major news/reputable sources
    const newsDomains = ['reuters.com', 'apnews.com', 'bbc.com', 'bbc.co.uk', 'npr.org',
      'nytimes.com', 'washingtonpost.com', 'wsj.com', 'economist.com', 'ft.com', 'bloomberg.com'];
    if (newsDomains.some(d => hostname.includes(d))) {
      return { type: 'news', label: 'NEWS', color: 'text-amber-400', bgColor: 'bg-amber-400/10 border-amber-400/30' };
    }

    return { type: 'standard', label: '', color: '', bgColor: '' };
  } catch {
    return { type: 'standard', label: '', color: '', bgColor: '' };
  }
}

/** Extract hostname from URL, stripping www. prefix */
export function getHostname(url: string): string {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url;
  }
}
